const formatUTCDateForChartLabels = (dateString: string): string => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(dateString));
};

export const groupMetricsByDateRange = <T extends Record<string, any>>(
  dataToGroup: T[],
  groupKey: keyof T,
  valueKey: keyof T,
  fromDateKey: keyof T,
  toDateKey: keyof T,
  isRange: boolean
) => {
  const groupedMap = new Map<
    string,
    { [key: string]: any; data: { y: any }[]; categories: string[] }
  >();

  for (const item of dataToGroup) {
    const groupValue = item[groupKey];
    const from = formatUTCDateForChartLabels(item[fromDateKey]);
    const to = formatUTCDateForChartLabels(item[toDateKey]);

    const rangeLabel = isRange ? `${from} – ${to}` : from;

    if (!groupedMap.has(groupValue)) {
      groupedMap.set(groupValue, {
        name: groupValue,
        data: [],
        categories: [],
      });
    }

    const group = groupedMap.get(groupValue)!;

    group.data.push({ y: item[valueKey] });
    group.categories.push(rangeLabel);
  }

  return Array.from(groupedMap.values());
};

export const groupMetricsByValue = <T extends Record<string, any>>(
  dataToGroup: T[],
  groupKey: keyof T,
  valueKey: keyof T,
  categoryKey: keyof T
) => {
  const groupedMap = new Map<
    string,
    { [key: string]: any; data: { y: any }[]; categories: string[] }
  >();

  for (const item of dataToGroup) {
    const groupValue = item[groupKey];

    if (!groupedMap.has(groupValue)) {
      groupedMap.set(groupValue, {
        name: groupValue,
        data: [],
        categories: [],
      });
    }

    const group = groupedMap.get(groupValue)!;

    group.data.push({ y: item[valueKey] });
    group.categories.push(item[categoryKey]);
  }

  return Array.from(groupedMap.values());
};

export const groupMetricsByKey = <
  T extends Record<string, any>,
  K extends keyof T,
  C extends Exclude<keyof T, K>,
>(
  dataToTransform: T[],
  groupKeys: K[],
  categoryKey: C,
  labelMap?: Partial<Record<K, string>>
) => {
  if (!dataToTransform?.length) return [];

  return groupKeys.map((metric) => ({
    name: labelMap?.[metric] ?? String(metric),
    data: dataToTransform.map((entry) => ({ y: entry[metric] })),
    categories: dataToTransform.map((entry) => entry[categoryKey]),
  }));
};

export const getCategories = (data: { categories: string[] }[] = []) =>
  data[0]?.categories?.length ? data[0].categories : [];

export const prepareSeries = (data) =>
  data.map(({ name, data }) => ({ name, data }));

type SharedTooltipContext = {
  key: string;
  points: {
    color: string;
    series: { name: string };
    y: number;
  }[];
};

export const buildSharedTooltip = (
  context: SharedTooltipContext,
  formatValue: (value: number) => string | JSX.Element
): string => {
  return `
    <div style="font-family: 'Jakarta', sans-serif; padding: 12px;">
      <span style="color: #140606; font-size: 18px; font-weight: 800; line-height: 1.5;">
        ${context.key}
      </span>
      <div style="height: 1px; width: 100%; background-color: #F0EEED; margin-bottom: 10px; margin-top: 8px;"></div>
      ${context.points
        ?.map(
          (point) => `
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
              <div>
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${
                  point.color
                };"></span>
                <span style="color: #50405E; font-size: 12px; font-weight: 600;">
                  ${point.series.name}:
                </span>
              </div>
              <div>
                <strong style="color: #3B0764; font-weight: 800; font-size: 14px;">
                  ${formatValue(point.y)}
                </strong>
              </div>
            </div>
          `
        )
        .join('')}
    </div>
  `;
};

export const groupStackedMetricsByDate = <T extends Record<string, any>>(
  data: T[] = [],
  stackKey: keyof T,
  valueKeys: (keyof T)[],
  fromDateKey: keyof T,
  toDateKey: keyof T,
  isRange = true
) => {
  const categories: string[] = [];
  const seriesMap = new Map<
    string,
    { name: string; stack: string; data: { y: number }[] }
  >();

  for (const item of data) {
    const stackName = String(item[stackKey]);
    const from = formatUTCDateForChartLabels(item[fromDateKey]);
    const to = formatUTCDateForChartLabels(item[toDateKey]);
    const dateLabel = isRange ? `${from} – ${to}` : from;
    if (!categories.includes(dateLabel)) {
      categories.push(dateLabel);
    }

    for (const key of valueKeys) {
      const seriesName = String(key);
      const mapKey = `${stackName}__${seriesName}`;

      if (!seriesMap.has(mapKey)) {
        seriesMap.set(mapKey, {
          name: seriesName,
          stack: stackName,
          data: [],
        });
      }

      const series = seriesMap.get(mapKey);
      series!.data.push({ y: item[key] ?? 0 });
    }
  }

  return {
    categories,
    series: Array.from(seriesMap.values()),
  };
};
